<template>
  <div class="slide-group">
    <!-- Left Arrow -->
    <button
      v-if="shouldShowArrows"
      class="slide-group__arrow"
      :disabled="!showLeftArrow"
      @click="scrollLeftClick"
      aria-label="Scroll left"
    >
      <i class="fas fa-chevron-left"></i>
    </button>

    <!-- Scroll Container -->
    <div
      ref="scrollContainer"
      class="slide-group__container"
      :class="{
        'slide-group__container--dragging': isDragging,
        'slide-group__container--drag-enabled': enableDrag,
      }"
      :style="containerStyle"
      @mousedown="handleMouseDown"
      @mousemove="handleMouseMove"
      @mouseup="handleMouseUp"
      @mouseleave="handleMouseLeave"
      @scroll="handleScroll"
    >
      <slot></slot>
    </div>

    <!-- Right Arrow -->
    <button
      v-if="shouldShowArrows"
      class="slide-group__arrow"
      :disabled="!showRightArrow"
      @click="scrollRightClick"
      aria-label="Scroll right"
    >
      <i class="fas fa-chevron-right"></i>
    </button>
  </div>
</template>

<script>
export default {
  name: "SlideGroup",
  props: {
    /**
     * Gap between items (CSS value)
     */
    gap: {
      type: String,
      default: "12px",
    },

    /**
     * Scroll snap type: 'mandatory' or 'proximity'
     */
    snapType: {
      type: String,
      default: "mandatory",
      validator: (value) => ["mandatory", "proximity"].includes(value),
    },

    /**
     * Whether to show navigation arrows.
     * Defaults to false on mobile (<=768px) and true on desktop.
     */
    showArrows: {
      type: Boolean,
      default: true,
    },

    /**
     * Amount to scroll when clicking arrows (in pixels)
     */
    scrollAmount: {
      type: Number,
      default: 200,
    },

    /**
     * Whether to enable drag-to-scroll functionality
     */
    enableDrag: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isDragging: false,
      startX: 0,
      scrollLeft: 0,
      dragDistance: 0,
      showLeftArrow: false,
      showRightArrow: false,
      windowWidth: typeof window !== "undefined" ? window.innerWidth : 1024,
    };
  },
  computed: {
    containerStyle() {
      return {
        gap: this.gap,
        scrollSnapType: this.isDragging ? "none" : `x ${this.snapType}`,
      };
    },
    isMobile() {
      return this.windowWidth <= 768;
    },
    shouldShowArrows() {
      if (
        this.$options.propsData &&
        Object.prototype.hasOwnProperty.call(
          this.$options.propsData,
          "showArrows"
        )
      ) {
        return this.showArrows;
      }
      return !this.isMobile;
    },
  },
  mounted() {
    this.updateArrowVisibility();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        this.updateArrowVisibility();
      });
      this.resizeObserver.observe(this.$refs.scrollContainer);
    }
    if (typeof window !== "undefined") {
      window.addEventListener("resize", this.handleResize);
    }
  },
  beforeDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("resize", this.handleResize);
    }
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    handleMouseDown(e) {
      if (!this.enableDrag || e.button !== 0) return;

      this.isDragging = true;
      this.startX = e.pageX;
      this.scrollLeft = this.$refs.scrollContainer.scrollLeft;
      this.dragDistance = 0;
    },
    handleMouseMove(e) {
      if (!this.enableDrag || !this.isDragging) return;

      e.preventDefault();
      const x = e.pageX;
      const walk = x - this.startX;
      this.dragDistance = Math.abs(walk);

      this.$refs.scrollContainer.scrollLeft = this.scrollLeft - walk;
    },
    handleMouseUp(e) {
      if (!this.enableDrag || !this.isDragging) return;

      if (this.dragDistance >= 5) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.isDragging = false;
      this.dragDistance = 0;
    },
    handleMouseLeave() {
      if (!this.enableDrag || !this.isDragging) return;

      this.isDragging = false;
      this.dragDistance = 0;
    },
    scrollLeftClick() {
      this.$refs.scrollContainer.scrollBy({
        left: -this.scrollAmount,
        behavior: "smooth",
      });
    },
    scrollRightClick() {
      this.$refs.scrollContainer.scrollBy({
        left: this.scrollAmount,
        behavior: "smooth",
      });
    },
    updateArrowVisibility() {
      const container = this.$refs.scrollContainer;
      if (!container) return;

      const { scrollLeft, scrollWidth, clientWidth } = container;

      this.showLeftArrow = scrollLeft > 0;
      this.showRightArrow = scrollLeft < scrollWidth - clientWidth - 1;
    },
    handleScroll() {
      this.updateArrowVisibility();
      this.$emit("scroll", {
        scrollLeft: this.$refs.scrollContainer.scrollLeft,
        scrollWidth: this.$refs.scrollContainer.scrollWidth,
        clientWidth: this.$refs.scrollContainer.clientWidth,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.slide-group {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;

  &__container {
    flex: 1;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
    cursor: default;

    &::-webkit-scrollbar {
      display: none;
    }

    &--drag-enabled {
      cursor: grab;
    }

    &--dragging {
      cursor: grabbing;
      user-select: none;
    }

    &--dragging * {
      user-select: none;
    }
  }

  &__arrow {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.95);
    border: 1px solid #e0e0e0;
    color: #333;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    &:not(:disabled):hover {
      background: #fff;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      transform: scale(1.05);
    }

    &:not(:disabled):active {
      transform: scale(0.95);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
      background: rgba(255, 255, 255, 0.6);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
    }

    i {
      font-size: 14px;
    }

    @media (max-width: 768px) {
      width: 32px;
      height: 32px;

      i {
        font-size: 12px;
      }
    }
  }
}
</style>
